import { GoogleMapsLoadError, loadGoogleMaps } from './google-maps-loader.mjs?v=20260806.100';

function escapeHtml(value) {
  return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

function coordinate(value) {
  const result = typeof value === 'function' ? value() : value;
  return Number.isFinite(Number(result)) ? Number(result) : null;
}

export function mapSelectionPayload(place) {
  const location = place?.geometry?.location || place?.location;
  const latitude = coordinate(location?.lat ?? place?.latitude);
  const longitude = coordinate(location?.lng ?? place?.longitude);
  const formattedAddress = String(place?.formatted_address || place?.formattedAddress || '').trim();
  if (!formattedAddress || latitude === null || longitude === null) return null;
  return Object.freeze({
    formattedAddress,
    latitude,
    longitude,
    placeId: String(place?.place_id || place?.placeId || '').trim(),
  });
}

export function dashboardMapTabMarkup({ address = '', editable = false } = {}) {
  const normalized = String(address || '').trim();
  return `<section class="dashboard-map-tab" data-dashboard-map-tab aria-label="Google Maps address">
    <div class="dashboard-map-tab__map">
      <div class="dashboard-map-tab__canvas" data-map-canvas aria-label="Interactive Google map"></div>
      <div class="dashboard-map-tab__status" data-map-status role="status">Loading map…</div>
      <form class="dashboard-map-tab__search" data-map-address-form>
        <svg class="dashboard-map-tab__search-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4 4"/></svg>
        <label><span class="sr-only">Address</span><input data-map-address aria-label="Search Google Maps" placeholder="Search Google Maps" value="${escapeHtml(normalized)}" ${editable ? 'autocomplete="street-address"' : 'readonly aria-readonly="true"'}></label>
        ${editable ? '<button type="submit" data-map-update disabled>Update</button>' : ''}
      </form>
    </div>
  </section>`;
}

export function mapLoadMessage(error) {
  // Cache-busted ESM URLs can create two equivalent GoogleMapsLoadError
  // constructors, so do not rely on cross-module instanceof identity here.
  const code = error?.name === 'GoogleMapsLoadError' && typeof error?.code === 'string'
    ? error.code
    : '';
  if (code === 'MAPS_KEY_MISSING') return 'Live map is not configured for this environment.';
  if (code === 'MAPS_ORIGIN_UNSUPPORTED') return 'Open AY TMS through its http/https address to use Google Maps.';
  if (code === 'MAPS_LOAD_TIMEOUT') return 'Google Maps took too long to load. Try again.';
  if (code === 'MAPS_SCRIPT_FAILED') return 'Google Maps is temporarily unavailable.';
  if (code === 'MAPS_RESPONSE_INVALID') return 'Google Maps returned an unusable response. Try again.';
  if (code === 'MAPS_ENVIRONMENT_UNAVAILABLE') return 'Live map is unavailable in this environment.';
  if (code === 'MAPS_KEY_CONFLICT') return 'Live map configuration changed. Refresh and try again.';
  return 'This map could not be loaded.';
}

export function mapDisplayOptions({ compact = false } = {}) {
  return Object.freeze({
    clickableIcons: true,
    disableDefaultUI: Boolean(compact),
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: true,
    gestureHandling: 'cooperative',
  });
}

export function mountDashboardMapTab({
  root,
  address = '',
  editable = false,
  apiKey = globalThis.KM_TMS_GOOGLE_MAPS_API_KEY || '',
  loader = loadGoogleMaps,
  onUpdate = async () => {},
} = {}) {
  const canvas = root?.querySelector?.('[data-map-canvas]');
  const status = root?.querySelector?.('[data-map-status]');
  const form = root?.querySelector?.('[data-map-address-form]');
  const input = root?.querySelector?.('[data-map-address]');
  const update = root?.querySelector?.('[data-map-update]');
  if (!canvas || !status || !form || !input || typeof loader !== 'function' || typeof onUpdate !== 'function') {
    throw new TypeError('Dashboard map tab options are invalid.');
  }
  let destroyed = false;
  let pending = null;
  let marker = null;
  let autocompleteListener = null;
  let autocompleteCleanup = () => {};
  let autocomplete = null;
  let map = null;
  const view = root?.ownerDocument?.defaultView || globalThis;
  const compactMap = Boolean(view?.matchMedia?.('(max-width: 720px)')?.matches);

  const setStatus = (message, state = '') => {
    if (destroyed) return;
    status.textContent = message;
    status.dataset.state = state;
  };
  const setSelection = (selection) => {
    if (!selection || destroyed) return;
    pending = selection;
    input.value = selection.formattedAddress;
    if (update) update.disabled = false;
    const position = { lat: selection.latitude, lng: selection.longitude };
    map?.setCenter?.(position);
    map?.setZoom?.(15);
    if (marker?.setPosition) marker.setPosition(position);
    else if (marker) marker.position = position;
    setStatus(editable ? 'Address selected. Choose Update to save.' : selection.formattedAddress, 'ready');
  };

  const onTextInput = () => {
    pending = null;
    if (update) update.disabled = true;
    setStatus('Choose a Google Maps suggestion before updating.', 'select');
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!editable || !pending || update?.disabled) return;
    update.disabled = true;
    setStatus('Updating address…', 'saving');
    try {
      await onUpdate({ ...pending });
      if (!destroyed) setStatus('Address updated.', 'saved');
    } catch {
      if (!destroyed) {
        update.disabled = false;
        setStatus('Address could not be updated. Try again.', 'error');
      }
    }
  };
  form.addEventListener('submit', onSubmit);
  if (editable) input.addEventListener('input', onTextInput);

  const ready = Promise.resolve()
    .then(() => loader({ apiKey }))
    .then(async (maps) => {
      if (destroyed) return;
      const mapsLibrary = typeof maps.importLibrary === 'function' ? await maps.importLibrary('maps') : maps;
      const placesLibrary = editable && typeof maps.importLibrary === 'function'
        ? await maps.importLibrary('places') : maps.places;
      const MapClass = mapsLibrary?.Map || maps.Map;
      if (typeof MapClass !== 'function') throw new GoogleMapsLoadError('MAPS_RESPONSE_INVALID');
      const fallbackCenter = { lat: 33.7701, lng: -118.1937 };
      map = new MapClass(canvas, {
        center: fallbackCenter,
        zoom: 11,
        ...mapDisplayOptions({ compact: compactMap }),
      });
      const MarkerClass = maps.Marker;
      if (typeof MarkerClass === 'function') marker = new MarkerClass({ map, position: fallbackCenter });

      if (editable) {
        const PlaceAutocompleteElementClass = placesLibrary?.PlaceAutocompleteElement;
        if (typeof PlaceAutocompleteElementClass === 'function') {
          autocomplete = new PlaceAutocompleteElementClass({});
          autocomplete.className = 'dashboard-map-tab__places-control';
          autocomplete.placeholder = 'Search Google Maps';
          autocomplete.value = input.value;
          input.hidden = true;
          input.insertAdjacentElement('afterend', autocomplete);
          const onPlaceSelect = async (event) => {
            try {
              const place = event?.placePrediction?.toPlace?.();
              await place?.fetchFields?.({ fields: ['formattedAddress', 'location', 'id'] });
              const selection = mapSelectionPayload(place);
              if (selection) setSelection(selection);
              else setStatus('Choose a complete address from Google Maps.', 'error');
            } catch {
              setStatus('The selected address could not be loaded.', 'error');
            }
          };
          autocomplete.addEventListener('gmp-select', onPlaceSelect);
          autocomplete.addEventListener('input', onTextInput);
          autocompleteCleanup = () => {
            autocomplete?.removeEventListener?.('gmp-select', onPlaceSelect);
            autocomplete?.removeEventListener?.('input', onTextInput);
          };
        } else {
          const AutocompleteClass = placesLibrary?.Autocomplete || maps.places?.Autocomplete;
          if (typeof AutocompleteClass !== 'function') throw new GoogleMapsLoadError('MAPS_RESPONSE_INVALID');
          autocomplete = new AutocompleteClass(input, {
            fields: ['formatted_address', 'geometry', 'place_id'],
            types: ['address'],
          });
          autocomplete.bindTo?.('bounds', map);
          autocompleteListener = autocomplete.addListener?.('place_changed', () => {
            const selection = mapSelectionPayload(autocomplete.getPlace?.());
            if (selection) setSelection(selection);
            else setStatus('Choose a complete address from Google Maps.', 'error');
          });
        }
      }

      const initialAddress = String(address || '').trim();
      const GeocoderClass = maps.Geocoder;
      if (initialAddress && typeof GeocoderClass === 'function') {
        const geocoder = new GeocoderClass();
        geocoder.geocode({ address: initialAddress }, (results, geocodeStatus) => {
          if (destroyed || geocodeStatus !== 'OK' || !results?.[0]) {
            if (!destroyed) setStatus(editable ? 'Search for an accurate address.' : initialAddress || 'No address recorded.', 'ready');
            return;
          }
          const selection = mapSelectionPayload(results[0]);
          if (!selection) return;
          const position = { lat: selection.latitude, lng: selection.longitude };
          map?.setCenter?.(position);
          map?.setZoom?.(15);
          if (marker?.setPosition) marker.setPosition(position);
          if (!editable) setStatus(selection.formattedAddress, 'ready');
          else setStatus('Search and choose an address to update.', 'ready');
        });
      } else {
        setStatus(editable ? 'Search and choose an address to update.' : 'No address recorded.', 'ready');
      }
    })
    .catch((error) => {
      setStatus(mapLoadMessage(error), 'error');
      canvas.dataset.mapUnavailable = 'true';
    });

  return Object.freeze({
    ready,
    selection: () => pending && { ...pending },
    destroy() {
      destroyed = true;
      form.removeEventListener('submit', onSubmit);
      input.removeEventListener('input', onTextInput);
      autocompleteCleanup();
      autocompleteListener?.remove?.();
      marker?.setMap?.(null);
      autocomplete = null;
      marker = null;
      map = null;
    },
  });
}
